import { conversationStudy, measurementContext } from '../context';
import { VoiceButton } from '../voice';
import type { ReportPart } from './logic';
import { sendLabel, sendToReport } from './send';
import { type Composed, SECTION_LABELS } from './shapes';
import { busy, button, el, onPanelHidden, type RadiologistDeps, toolCard, toolFailure, traceNodes } from './ui';

/**
 * "Redactar informe" (contract v3 §2.3): the reader dictates or types what they see, ris writes the
 * sections of the template the reading screen would load (doctor before org) and the card previews
 * each one. Nothing is stored by composing: the reader ticks the sections to keep and "Enviar al
 * informe" appends them to the draft through §2.1, after the confirmation shows all of it.
 */

export const COMPOSE_TEXT = {
  tool: 'Redactar informe',
  title: 'Redactar informe con la plantilla',
  hint: 'Dicte o escriba lo que observa. El Asistente Guru redacta cada sección de su plantilla; usted elige cuáles enviar.',
  label: 'Dictado para el informe',
  placeholder: 'Ejemplo: nódulo de 8 mm en el lóbulo superior derecho, sin adenopatías…',
  compose: 'Redactar',
  composing: 'Redactando…',
  template: (name: string) => `Plantilla: ${name}`,
  noTemplate: 'Sin plantilla configurada: secciones generales.',
  empty: 'El Asistente Guru no devolvió secciones para el informe.',
  noneSelected: 'Elija al menos una sección para enviar.',
  failed: 'No fue posible redactar el informe.',
  disclaimer: 'Borrador sugerido · revise cada sección · no firma',
} as const;

const MAX_DICTATION = 8000;

function sectionRow(part: ReportPart, index: number): HTMLElement {
  const row = el('div', `assistant-compose-section section-${part.section}`);
  row.dataset.section = part.section;
  const box = el('input', 'assistant-compose-pick');
  box.type = 'checkbox';
  box.checked = true;
  box.id = `assistant-compose-${index}-${Math.random().toString(36).slice(2, 8)}`;
  const label = el('label', 'assistant-report-section-title', SECTION_LABELS[part.section]);
  label.htmlFor = box.id;
  const head = el('div', 'assistant-compose-head');
  head.append(box, label);
  row.append(head, el('div', 'assistant-report-section-text', part.text));
  return row;
}

function renderComposed(deps: RadiologistDeps, study: string, composed: Composed, host: HTMLElement): void {
  if (composed.sections.length === 0) return void host.replaceChildren(el('p', 'assistant-empty', COMPOSE_TEXT.empty));
  const parts: ReportPart[] = composed.sections.map((s) => ({ section: s.section, text: s.text }));
  const rows = parts.map(sectionRow);
  const send = button(sendLabel(), 'assistant-compose-send', () => {
    const chosen = parts.filter((_, i) => rows[i].querySelector<HTMLInputElement>('.assistant-compose-pick')?.checked);
    if (chosen.length === 0) return void deps.shell.toast(COMPOSE_TEXT.noneSelected, 'warning');
    void sendToReport(deps.shell, deps.api, chosen, study);
  });
  host.replaceChildren(
    el('div', 'assistant-trace', composed.template ? COMPOSE_TEXT.template(composed.template.name) : COMPOSE_TEXT.noTemplate),
    ...rows,
    ...traceNodes(composed),
    send,
  );
}

function dictationField(): HTMLTextAreaElement {
  const field = el('textarea', 'assistant-ask-input assistant-compose-input');
  field.rows = 5;
  field.maxLength = MAX_DICTATION;
  field.placeholder = COMPOSE_TEXT.placeholder;
  field.setAttribute('aria-label', COMPOSE_TEXT.label);
  return field;
}

export function showCompose(deps: RadiologistDeps): void {
  const study = conversationStudy(deps.shell);
  const { body } = toolCard(deps, 'compose', COMPOSE_TEXT.title, study);
  const field = dictationField();
  const voice = new VoiceButton(deps.shell, deps.api, (text) => {
    field.value = [field.value.trim(), text.trim()].filter(Boolean).join(' ').slice(0, MAX_DICTATION);
    field.focus();
  }, () => study);
  const output = el('div', 'assistant-compose-output');
  output.setAttribute('aria-live', 'polite');
  const compose = button(COMPOSE_TEXT.compose, 'assistant-compose-run', () => void busy(compose, COMPOSE_TEXT.composing, async () => {
    try {
      const composed = await deps.api.reportCompose(study, field.value.trim().slice(0, MAX_DICTATION), measurementContext(study));
      renderComposed(deps, study, composed, output);
    } catch (error) {
      console.warn('[viewer] report compose failed', error);
      deps.shell.toast(toolFailure(error, 'report_compose', COMPOSE_TEXT.failed), 'error');
    }
  }));
  const row = el('div', 'assistant-finding-actions');
  if (voice.element) row.appendChild(voice.element);
  row.appendChild(compose);
  body.append(el('p', 'assistant-tool-note', COMPOSE_TEXT.hint), el('p', 'assistant-tool-note', COMPOSE_TEXT.disclaimer), field, row, output);
  // A recording in progress is finalized (transcribed, or kept) when the Asistente tab is hidden, as the
  // panel's own dictation is: never discarded.
  onPanelHidden(deps.panel, () => void voice.finalize());
  field.focus();
}
